import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Button, Dialog, IconButton } from "@mui/material";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import { TableCellType } from "components/LazyTable";
import SnackbarMessage from "components/SnackbarMessage";
import SortableTable from "components/SortableTable";
import { FORM_ERROR } from "final-form";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useCallback, useMemo, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  AttributeFilter,
  ListRequestOfAttributeFilter,
  ListResponseOfSkuAttributeByType,
  OrganizationAttributeType,
  SkuAttribute,
  SkuAttributeRequest,
} from "services/KnowfullyAdminClient";
import { AttributeCodeConstants, Severity, SnackbarMessageProps } from "types/common-types";
import SkuCoverImageField from "components/SkuCoverImageField";
import {
  SkuAttributeByTypeWithId,
  SkuAttributeExtendedWithId,
  SkuFormProps,
} from "types/sku-related-types";
import translateError from "utils/translateError";
import {
  buildAttributeDataCells,
  flattenSponsorMetaValues,
} from "./attributeColumnsUtils";
import SkuAttributeForm from "./SkuAttributeForm";

type SkuAttributeFormValues = {
  organizationAttributeType?: OrganizationAttributeType;
  attributeId?: string;
};

const SkuAttributeAccordion = ({
  sku,
  onSkuUpdated,
  onDirty,
  onClean,
  organizationAttributeType,
  ...props
}: SkuFormProps & SkuAttributeFormValues) => {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const { skuService } = useGlobalContext();
  const [deleting, setDeleting] = useState<SkuAttribute | null>(null);
  const [deleteOpen, deleteOpenActions] = useBoolean(false);
  const [createOpen, createOpenActions] = useBoolean(false);

  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const request = ListRequestOfAttributeFilter.fromJS({
    sortField: "createdOn",
    sortDescending: true,
    parameters: AttributeFilter.fromJS({
      organizationAttributeTypeId: organizationAttributeType?.id,
    }),
  });

  const { data, isFetching } = useStrictQuery<ListResponseOfSkuAttributeByType>(
    ["skuAttributesByType", sku.id, request],
    () => skuService.getSkuAttributesByType(sku.id!, request)
  );

  const skuAttributesByType = useMemo(
    () => (data?.data ?? []) as SkuAttributeByTypeWithId[],
    [data?.data]
  );

  const codeConstant = organizationAttributeType?.attributeType?.codeConstant?.toLowerCase();
  const allowDates = !!organizationAttributeType?.attributeType?.allowDates;

  const skuAttributes = useMemo(() => {
    const group = data?.data?.find(
      (g) => g.organizationAttributeTypeId === organizationAttributeType?.id
    );
    const raw = (group?.attributes ?? []) as SkuAttributeExtendedWithId[];
    if (codeConstant === AttributeCodeConstants.Sponsors) {
      return flattenSponsorMetaValues(raw, t);
    }
    return raw;
  }, [data?.data, codeConstant, organizationAttributeType?.id, t]);

  const handleDelete = useCallback(async () => {
    const id = deleting?.id;
    setDeleting(null);
    if (id) {
      try {
        await registerPromise(skuService.deleteSkuAttribute(id));
        queryClient.invalidateQueries(["skuAttributes"]);
        queryClient.invalidateQueries(["skuAttributesByType"]);

        setSnackbarMessage(
          new SnackbarMessageProps(
            t({ id: "skuAttributeDeleted" }),
            Severity.Warning
          )
        );

        deleteOpenActions.setFalse();
      } catch (err: any) {
        return { [FORM_ERROR]: translateError(err, t) };
      }
    }
  }, [
    deleteOpenActions,
    deleting?.id,
    queryClient,
    registerPromise,
    skuService,
    t,
  ]);

  // Build cells dynamically via util, then append the DeleteButton action column
  const cells: Array<TableCellType> = useMemo(() => {
    const DeleteButton = ({ value }: { value: SkuAttribute }) => {
      return (
        <Box textAlign="center">
          <IconButton
            size="small"
            onClick={() => {
              setDeleting(value);
              deleteOpenActions.setTrue();
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      );
    };

    const baseCells = buildAttributeDataCells({
      codeConstant,
      allowDates,
      defaultAccessor: "displayText",
      t,
    });

    baseCells.push({
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: DeleteButton,
      label: "",
      noSort: true,
    });

    return baseCells;
  }, [codeConstant, allowDates, deleteOpenActions, t]);

  const onAttributeCreated = () => {
    onSkuUpdated && onSkuUpdated();
    createOpenActions.setFalse();

    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "skuAttributeCreated" }))
    );
  };

  const isCoverImage = organizationAttributeType?.attributeType?.codeConstant?.toLowerCase() === AttributeCodeConstants.CoverImage;

  const [existingCoverImage] = skuAttributes;

  const handleCoverImageChange = async (url?: string) => {
    try {
      if (!url && existingCoverImage) {
        await registerPromise(skuService.deleteSkuAttribute(existingCoverImage.id));
      } else if (url) {
        await registerPromise(
          skuService.saveSkuAttribute(
            sku.id!,
            SkuAttributeRequest.fromJS({
              id: existingCoverImage?.id,
              value: url,
              organizationAttributeTypeId: organizationAttributeType?.id,
            })
          )
        );
      }
      queryClient.invalidateQueries(["skuAttributes"]);
      queryClient.invalidateQueries(["skuAttributesByType"]);
      onSkuUpdated && onSkuUpdated();
    } catch (err: any) {
      setSnackbarMessage(
        new SnackbarMessageProps(translateError(err, t), Severity.Error)
      );
    }
  };

  if (isCoverImage) {
    return (
      <>
        <Box my={2}>
          <SkuCoverImageField
            value={existingCoverImage?.value || undefined}
            onChange={handleCoverImageChange}
          />
        </Box>
        <SnackbarMessage
          snackbarMessage={snackbarMessage}
          setSnackbarMessage={setSnackbarMessage}
        />
      </>
    );
  }

  return (
    <>
      <Box my={2} textAlign="right">
        <Button
          variant="outlined"
          color="primary"
          onClick={createOpenActions.setTrue}
        >
          <T
            id="addEntity"
            values={{
              entity: organizationAttributeType?.attributeType?.name,
            }}
          />
        </Button>
      </Box>

      <SortableTable
        {...{
          rows: skuAttributes,
          loading: isFetching,
          dense: true,
          cells,
        }}
      />
      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <SkuAttributeForm
          {...props}
          sku={sku}
          organizationAttributeType={organizationAttributeType}
          setSnackbarMessage={setSnackbarMessage}
          skuAttributes={skuAttributesByType}
          onAttributeCreated={onAttributeCreated}
          onCancel={createOpenActions.setFalse}
        />
      </Dialog>

      <DeleteEntityDialog
        dialogTitleKey={"attributeDeleteTitle"}
        contentKey={"attributeDeleteQuestion"}
        onCancel={deleteOpenActions.setFalse}
        handleDelete={handleDelete}
        open={deleteOpen}
      />
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
};

export default withErrorBoundary(SkuAttributeAccordion);
